// ClickToPay.js
// Base class for vendor SDK integrations
const { sdkFetch } = require('../http');
const { mainConfigService } = require('../services/config');
const { WebSocketService, CallbackService } = require('../services');

class ClickToPay {
  // Static map to store fetched responses
  fetchedResponses = new Map();
  // Static map to store ongoing initialization promises
  initializationPromises = new Map();

  constructor(
    submitUrl,
    paymentMethod,
    currScript,
    fieldsService
  ) {
    this.submitUrl = submitUrl;
    this.paymentMethod = paymentMethod;
    this.fieldsService = fieldsService;
    this.webSocketService = new WebSocketService(this.fieldsService);
    this.callbackService = new CallbackService(currScript, fieldsService);
  }

  async initialize() {
    const response = await this._performInitialization();
    this.handlePayment(response);
  }

  async _performInitialization() {
    try {
      const response = await this.fetchVendorScript();

      await this.loadVendorScript(response.js_sdk_src || response.sdk_client.src);

      return response;
    } catch (error) {
      console.error('Error initializing ClickToPay integration:', error);
      throw error;
    }
  }

  async fetchVendorScript() {
    // Use sdkFetch and error handling conventions from http.js
    const res = await sdkFetch(this.submitUrl, {
      method: 'POST',
      headers: {
        Authorization: `Api-Key ${mainConfigService.get('apiKey')}`,
        'Accept-Language': mainConfigService.get('lang'),
      },
    });
    if (res.ok) {
      return res.json();
    } else {
      if (res.status >= 500) {
        throw res;
      } else {
        throw await res.json();
      }
    }
  }

  async loadVendorScript(src) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  async waitForDependency(retries = 20, interval = 1000) {
    return new Promise((resolve, reject) => {
      const check = () => {
        try {
          if (this.checkDependency()) {
            resolve();
          } else if (retries > 0) {
            setTimeout(() => check(--retries), interval);
          } else {
            console.error('Dependency not found.');
            reject(new Error('Dependency not found.'));
          }
        } catch (err) {
          console.error('Error while checking dependency:', err);
          reject(err);
        }
      };
      check();
    });
  }

  // Abstract method to be implemented by subclasses
  async handlePayment() {
    throw new Error('handlePayment() must be implemented by subclass');
  }

  // Abstract method to be implemented by subclasses
  checkDependency() {
    throw new Error('checkDependency() must be implemented by subclass');
  }

  /**
   * Handle WebSocket connection for payment processing
   * @param {Object} response - Payment response containing ws_url and reference_number
   * @param {Object} callbacks - Callback functions for WebSocket events (optional)
   * @returns {Promise<boolean>} - Returns true if WebSocket connection is successful
   */
  async handleWebSocket(response) {
    if (!response.ws_url || !response.reference_number) {
      console.warn('WebSocket URL or reference number not found in response');
      return false;
    }

    const webSocketCallbacks = this.callbackService.getWebSocketCallbacks();

    try {
      await this.webSocketService.connect({
        wsUrl: response.ws_url,
        referenceNumber: response.reference_number,
        merchantId: mainConfigService.get('merchant_id'),
        callbacks: webSocketCallbacks,
      });
      return true;
    } catch (error) {
      console.error('WebSocket connection failed:', error);
      if (webSocketCallbacks.onError) {
        webSocketCallbacks.onError({
          status: 'error',
          message: 'WebSocket connection failed',
          originalError: error,
        });
      }
      return false;
    }
  }
}

module.exports = ClickToPay;
